import type { FieldTypeFilterConfig, FieldTypeTemplate, PrimitiveType } from '#layers/data-model/public';
import type { FilterSchemaPrimitiveFieldMeta } from './filter-schema';
import { callOrReturn } from '#layers/base/public';
import { getPrimitiveFieldPresentationDefaults, getTypename } from '#layers/data-model/public';
import DateTimeFilterInput from '#layers/filters/components/DateTimeFilter/FilterInput.vue';
import DateTimeFilterInputGroup from '#layers/filters/components/DateTimeFilter/FilterInputGroup.vue';

/**
 * Minimal field shape consumed by resolveFilterConfig. Both FilterSchemaPrimitiveField
 * and the structural types used by useLogicalFilter are assignable.
 */
export interface FilterConfigResolvableField {
	type: PrimitiveType;
	meta?: FilterSchemaPrimitiveFieldMeta;
}

const filterPresentationByTypeTemplate: Record<string, Pick<FieldTypeFilterConfig, 'input' | 'inputGroup'>> = {
	datetime: {
		input: DateTimeFilterInput,
		inputGroup: DateTimeFilterInputGroup,
	},
};

export function resolveFilterConfig(
	field: FilterConfigResolvableField | undefined,
	fieldTypeTemplates: Record<string, FieldTypeTemplate>,
): FieldTypeFilterConfig | undefined {
	if (!field) return undefined;

	const primitiveFilterDefaults = getPrimitiveFieldPresentationDefaults(field.type).filter;
	const typeTemplateId = field.meta?.typeTemplateId ?? primitiveFilterDefaults?.typeTemplateId;

	if (!typeTemplateId) return undefined;

	const template = fieldTypeTemplates[typeTemplateId];

	if (!template || template.type !== 'primitive' || !('filter' in template) || !template.filter) {
		return undefined;
	}

	const filterConfig = callOrReturn(template.filter, {
		options: field.meta?.typeOptions ?? undefined,
		primitiveType: getTypename(field.type),
	});

	const filterPresentation = filterPresentationByTypeTemplate[typeTemplateId];

	return filterPresentation
		? { ...filterPresentation, ...filterConfig }
		: filterConfig;
}
